// src/pages/SubscriptionPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import algosdk from 'algosdk';
import { PeraWalletConnect } from '@perawallet/connect';

import apiClient from '../api';
import '../styles/SubscriptionPage.css';

const peraWallet = new PeraWalletConnect();

// Public AlgoNode endpoints — fine to call from the browser (no token required for free tier).
// Default to mainnet to match the backend; opt into testnet by setting REACT_APP_ALGORAND_NETWORK=testnet.
const ALGOD_URL =
  process.env.REACT_APP_ALGOD_URL ||
  (process.env.REACT_APP_ALGORAND_NETWORK === 'testnet'
    ? 'https://testnet-api.algonode.cloud'
    : 'https://mainnet-api.algonode.cloud');

const microToUsdc = (micro) => (Number(micro) / 1_000_000).toFixed(2);

function StatusCard({ state }) {
  if (!state || state.status === 'none' || !state.tier) {
    return (
      <div className="sub-status-card sub-status-none">
        <strong>No active subscription.</strong>
        <p>Pick a tier below to activate your account.</p>
      </div>
    );
  }
  const expires = state.expires_at ? new Date(state.expires_at).toLocaleDateString() : '—';
  const clsByStatus = {
    active: 'sub-status-active',
    demo: 'sub-status-demo',
    read_only: 'sub-status-readonly',
    expired: 'sub-status-expired',
  };
  const cls = clsByStatus[state.status] || 'sub-status-none';

  if (state.status === 'demo') {
    return (
      <div className={`sub-status-card ${cls}`}>
        <h3>Demo mode</h3>
        <p>
          You can host up to <strong>{state.user_cap}</strong> users with off-chain points only.
          Demo ends on <strong>{expires}</strong>{' '}
          {state.days_remaining != null && `(${state.days_remaining} day(s) remaining)`}.
        </p>
        <p className="sub-warning">
          When the demo ends, your program(s) will be <strong>deleted</strong> unless you subscribe.
          On-chain ASA distribution and higher user caps unlock with a paid plan.
        </p>
      </div>
    );
  }

  return (
    <div className={`sub-status-card ${cls}`}>
      <h3>Current plan: {state.tier?.toUpperCase()}</h3>
      <p>
        Status: <strong>{state.status}</strong> · User cap: <strong>{state.user_cap}</strong> ·
        Expires: <strong>{expires}</strong>{' '}
        {state.days_remaining != null && `(${state.days_remaining} day(s) remaining)`}
      </p>
      {state.status === 'read_only' && (
        <p className="sub-warning">Your subscription expired — renew to resume issuing points.</p>
      )}
    </div>
  );
}

function SubscriptionPage({ isAuthenticated }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [addonQuantity, setAddonQuantity] = useState(0);

  const loadState = useCallback(async () => {
    try {
      const r = await apiClient.get('/admin/subscription/status');
      setState(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadState();
  }, [isAuthenticated, loadState]);

  const buy = async (tier) => {
    setError('');
    setMessage('');
    setWorking(true);
    let address = '';
    try {
      // 1) Connect Pera. reconnectSession() can leave a stale connector reference behind
      // (e.g. when localStorage has a session but the bridge is dead), which makes the
      // very next connect() throw "Session currently connected". Always clear stale state
      // before connect() unless reconnect actually returned an account.
      try {
        const accounts = await peraWallet.reconnectSession();
        address = accounts?.[0] || '';
      } catch {
        address = '';
      }
      if (!address) {
        try { await peraWallet.disconnect(); } catch { /* nothing to disconnect */ }
        const accounts = await peraWallet.connect();
        if (!accounts?.length) throw new Error('Pera Wallet connection cancelled.');
        address = accounts[0];
      }

      // 2) get a quote from the backend
      setMessage('Requesting quote...');
      const quoteResp = await apiClient.post('/admin/subscription/quote', {
        tier,
        addon_quantity: addonQuantity,
      });
      const quote = quoteResp.data;

      // 3) build the asset-transfer transaction
      setMessage(`Preparing payment of ${microToUsdc(quote.total_amount_micro)} USDCa...`);
      const algod = new algosdk.Algodv2('', ALGOD_URL, '');
      const params = await algod.getTransactionParams().do();
      const noteBytes = new TextEncoder().encode(quote.note);

      const txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        sender: address,
        receiver: quote.receiver_address,
        amount: quote.total_amount_micro,
        assetIndex: quote.asset_id,
        note: noteBytes,
        suggestedParams: params,
      });

      // 4) ask Pera to sign + submit (Pera handles submission)
      setMessage('Open Pera Wallet to confirm the payment...');
      const signedTxns = await peraWallet.signTransaction([[{ txn, signers: [address] }]]);
      const { txid } = await algod.sendRawTransaction(signedTxns).do();

      setMessage(`Payment broadcast (tx ${txid.slice(0, 10)}…). Waiting for confirmation...`);
      await algosdk.waitForConfirmation(algod, txid, 6);

      // 5) verify with backend
      setMessage('Verifying on-chain payment with the server...');
      const verifyResp = await apiClient.post('/admin/subscription/verify', {
        tx_id: txid,
        note: quote.note,
      });
      setState(verifyResp.data);
      setMessage('Subscription activated. Thank you!');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      setError(`Subscription failed: ${detail}`);
      setMessage('');
    } finally {
      setWorking(false);
    }
  };

  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (loading) return <div style={{ padding: 24 }}>Loading subscription...</div>;

  const starterTotal = 50 + addonQuantity * 100;
  const proTotal = 200 + addonQuantity * 100;

  return (
    <div className="subscription-page">
      <h1>Subscription</h1>
      <StatusCard state={state} />

      <div className="addon-row">
        <label>
          Add-on user packs (+{addonQuantity * 200} users):
          <input
            type="number"
            min="0"
            max="50"
            value={addonQuantity}
            onChange={(e) => setAddonQuantity(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
            disabled={working}
          />
        </label>
        <span className="addon-help">100 USDCa per pack adds 200 users.</span>
      </div>

      <div className="tier-grid">
        <div className="tier-card">
          <h2>Starter</h2>
          <p className="tier-price">{starterTotal} USDCa / year</p>
          <ul>
            <li>50 base users {addonQuantity > 0 && `(+${addonQuantity * 200} add-on)`}</li>
            <li>1-year access</li>
            <li><strong>On-chain ASA distribution</strong> (mint your own token or use gramo)</li>
            <li>Apple &amp; Google Wallet passes</li>
          </ul>
          <button
            className="tier-btn"
            onClick={() => buy('starter')}
            disabled={working}
          >
            {working ? 'Processing...' : 'Pay with Pera Wallet'}
          </button>
        </div>

        <div className="tier-card tier-card-featured">
          <h2>Pro</h2>
          <p className="tier-price">{proTotal} USDCa / year</p>
          <ul>
            <li>500 base users {addonQuantity > 0 && `(+${addonQuantity * 200} add-on)`}</li>
            <li>1-year access</li>
            <li><strong>On-chain ASA distribution</strong> (mint your own token or use gramo)</li>
            <li>Apple &amp; Google Wallet passes</li>
            <li>Priority support</li>
          </ul>
          <button
            className="tier-btn tier-btn-primary"
            onClick={() => buy('pro')}
            disabled={working}
          >
            {working ? 'Processing...' : 'Pay with Pera Wallet'}
          </button>
        </div>
      </div>

      {message && <p className="info-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}

      <p className="sub-fineprint">
        Payments are made in USDCa on Algorand to{' '}
        <code>GRAMOCJVDAIZ5HZYIY5G7EPCI6XTT2NYXYJEV76JPLFQDTM7RQ2MYBNHUU</code>. Your wallet must
        be opted in to USDCa and hold enough balance.
      </p>
    </div>
  );
}

export default SubscriptionPage;
